import type { FeilSvar } from '@es/shared';
import { feilSvarSchema } from '@es/shared';
import type { ZodType } from 'zod';
import { miljo } from '../felles/miljo.js';
import { hentToken } from '../auth/oidc.js';

/** An error carrying the API's structured body, including the trace id. */
export class ApiFeil extends Error {
  constructor(
    readonly status: number,
    readonly svar: FeilSvar | null,
    melding: string,
  ) {
    super(melding);
    this.name = 'ApiFeil';
  }

  /** Safe to show a user — the API guarantees no internals leak into it. */
  get brukermelding(): string {
    return this.svar?.melding ?? 'Noe gikk galt. Prøv igjen.';
  }

  get sporingsId(): string | null {
    return this.svar?.sporingsId ?? null;
  }
}

/**
 * The single path from the browser to the API.
 *
 * Every response is parsed with the SAME Zod schema the backend validated it
 * against (§1). That is the point of the shared package: if the two ever drift,
 * it is a TypeScript error when this file is compiled, not a runtime surprise in
 * a kommune's finance department.
 */
export async function apiHent<T>(sti: string, skjema: ZodType<T>, init?: RequestInit): Promise<T> {
  const token = await hentToken();

  // Built as a Headers instance rather than an object spread: HeadersInit may
  // legitimately be an array of pairs, and spreading that into an object turns
  // it into numeric keys — silently dropping every header the caller passed.
  const headers = new Headers(init?.headers);
  headers.set('Accept', 'application/json');
  // FormData must NOT get an explicit Content-Type: the browser sets it along
  // with the multipart boundary, and overriding it makes the upload unparseable
  // on the server.
  if (init?.body !== undefined && !(init.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const svar = await fetch(`${miljo().apiBaseUrl}/api/v1${sti}`, { ...init, headers });

  if (!svar.ok) throw await tilApiFeil(svar, sti);

  // 204, eller 200 med tom kropp: det er ikke JSON, og `.json()` kaster på den.
  // Hvert endepunkt som svarer «gjort, ingenting å si» — deaktiver bruker, sett
  // tilganger, trekk invitasjon, opprett virksomhet — går denne veien, og uten
  // dette feilet de alle med en melding som aldri kom fra API-et.
  const tomKropp = svar.status === 204 || svar.headers.get('Content-Length') === '0';
  const data: unknown = tomKropp ? undefined : await svar.json();
  const resultat = skjema.safeParse(data);
  if (!resultat.success) {
    // A shape the client did not expect is a bug, not data — surfaced loudly
    // rather than rendered as undefined somewhere down the tree.
    throw new ApiFeil(
      svar.status,
      null,
      `Uventet svarformat fra ${sti}: ${resultat.error.issues
        .map((i) => `${i.path.join('.')}: ${i.message}`)
        .join('; ')}`,
    );
  }
  return resultat.data;
}

async function tilApiFeil(svar: Response, sti: string): Promise<ApiFeil> {
  const kropp: unknown = await svar.json().catch(() => null);
  const tolket = feilSvarSchema.safeParse(kropp);
  return new ApiFeil(
    svar.status,
    tolket.success ? tolket.data : null,
    `API-kall mot ${sti} feilet med ${String(svar.status)}`,
  );
}

/** Filnavnet i `Content-Disposition: attachment; filename="…"`, eller null. */
export function filnavnFra(disposisjon: string | null): string | null {
  return disposisjon?.match(/filename="([^"]+)"/)?.[1] ?? null;
}

/**
 * Laster ned en fil fra API-et, med innloggingen, og lar nettleseren lagre den.
 *
 * En vanlig lenke kan ikke brukes: den sender ikke tilgangstokenet. Filen
 * hentes derfor som en blob og lagres gjennom en lenke som lages og fjernes
 * igjen. Navnet er det serveren oppgir, og `reservenavn` bare hvis det mangler.
 */
/** En GET med tilgangstokenet, som et rått svar. Feiler som `apiHent`. */
async function hentMedToken(sti: string): Promise<Response> {
  const token = await hentToken();
  const headers = new Headers();
  if (token) headers.set('Authorization', `Bearer ${token}`);
  const svar = await fetch(`${miljo().apiBaseUrl}/api/v1${sti}`, { headers });
  if (!svar.ok) throw await tilApiFeil(svar, sti);
  return svar;
}

/**
 * En fil fra API-et, som en blob — et bilde i rapporten. Et `<img src>` kan
 * ikke sende tilgangstokenet, så bildet hentes slik og vises fra en objekt-URL.
 */
export async function apiBlob(sti: string): Promise<Blob> {
  return (await hentMedToken(sti)).blob();
}

export async function apiLastNed(sti: string, reservenavn: string): Promise<void> {
  const svar = await hentMedToken(sti);

  const url = URL.createObjectURL(await svar.blob());
  const lenke = document.createElement('a');
  lenke.href = url;
  lenke.download = filnavnFra(svar.headers.get('Content-Disposition')) ?? reservenavn;
  document.body.append(lenke);
  lenke.click();
  lenke.remove();
  URL.revokeObjectURL(url);
}

/**
 * Meldingen som skal vises til et menneske når et kall feiler.
 *
 * Finnes fordi det motsatte var tilfellet: skjermene brukte `feil.message`, som
 * er klientens egen tekniske streng — «API-kall mot /kommuner feilet med 400» —
 * mens API-ets faktiske svar lå urørt i `svar`. En bruker som glemte å fylle ut
 * et påkrevd felt fikk altså et statusnummer, ikke navnet på feltet, selv om
 * serveren hadde sendt begge deler.
 *
 * Feltfeilene tas med. Ved et skjema er det nettopp DE som er svaret: «Validering
 * feilet» alene forteller ingen hva de skal gjøre.
 */
export function feilmelding(feil: unknown, reserve = 'Noe gikk galt. Prøv igjen.'): string {
  if (feil instanceof ApiFeil) {
    const felter = feil.svar?.detaljer ?? [];
    if (felter.length > 0) {
      return `${feil.brukermelding}: ${felter.map((d) => `${d.felt} — ${d.melding}`).join(', ')}`;
    }
    return feil.brukermelding;
  }
  // Ikke en ApiFeil: en nettverksfeil eller en programmeringsfeil. Da er
  // `message` det beste vi har.
  return feil instanceof Error && feil.message !== '' ? feil.message : reserve;
}

/**
 * Om feilen er at noen andre lagret det samme først.
 *
 * Skilt ut fra annen 409: i prognosen betyr 409 også at en metode er låst, og
 * det er et nei — mens en versjonskonflikt er et spørsmål om hvilken versjon
 * som skal gjelde, og skjermen skal la brukeren velge.
 */
export function erVersjonskonflikt(feil: unknown): boolean {
  return feil instanceof ApiFeil && feil.svar?.feilkode === 'versjonskonflikt';
}
